import AnimateOnScroll from '../components/AnimateOnScroll';
import { daniel_petrov, eden_koresh, liron_lahav, neta_levy, yarin_hagai, guy_moshe, hagar_ben_amram, alma_dror } from '../assets/reviewers';
import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../providers/LanguageProvider';

const AUTO_EXPAND = false;
const MAX_COMMENT_LENGTH = 450;

const reviewerImages = {
  daniel_petrov,
  eden_koresh,
  liron_lahav,
  neta_levy,
  yarin_hagai,
  guy_moshe,
  hagar_ben_amram,
  alma_dror
};

const TestimonialCard = ({ review, translations, isExpanded, setIsExpanded, onUserInteraction }) => {
  const needsTruncation = review.comment.length > MAX_COMMENT_LENGTH;
  const displayText = isExpanded || !needsTruncation
    ? review.comment
    : review.comment.substring(0, MAX_COMMENT_LENGTH) + '...';

  return (
    <div className={`md:px-5 rounded-2xl transition-all duration-300 flex-shrink-0 w-full md:w-1/2 lg:w-1/3`}>
      <div className="text-center h-full flex flex-col">
        <div className="flex-1 flex items-center">
          <div className="text-neutral-700 leading-relaxed text-sm md:text-base italic font-montserrat whitespace-pre-line w-full">
            "{displayText}"
            {needsTruncation && !isExpanded && (
              <button
                onClick={() => {
                  setIsExpanded(true);
                  onUserInteraction();
                }}
                className="ml-2 text-mocha hover:text-mocha/80 font-medium text-sm underline"
              >
                {translations?.menu?.reviews?.show_more }
              </button>
            )}
          </div>
        </div>
        
        <div className="mt-10 flex flex-col items-center justify-center gap-4">
          <img
            src={reviewerImages[review.profile_image]}
            alt={review.name}
            className="w-24 h-24 md:w-20 md:h-20 rounded-full object-cover border-4 border-mocha shadow-lg"
          />
          <cite>
            <span className="font-semibold text-lg text-neutral-800">
              {review.name}
            </span>
          </cite>
        </div>
      </div>
    </div>
  );
};

const ReviewsSection = ({ id }) => {
  const { translations, isRTL } = useLanguage();
  const [reviews, setReviews] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [expandedCards, setExpandedCards] = useState({});
  const [itemsPerSlide, setItemsPerSlide] = useState(1);

  useEffect(() => {
    if (translations.reviews) {
      const shuffledReviews = translations.reviews.slice(0, 9).sort(() => Math.random() - 0.5);
      setReviews(shuffledReviews);
      
      // Auto-expand all long reviews if enabled
      const initialExpandedCards = {};
      if (AUTO_EXPAND) {
        shuffledReviews.forEach((review, index) => {
          if (review.comment.length > MAX_COMMENT_LENGTH) {
            initialExpandedCards[index] = true;
          }
        });
      }
      setExpandedCards(initialExpandedCards);
    }
  }, [translations.reviews]);

  useEffect(() => {
    const updateItemsPerSlide = () => {
      // Using Tailwind's default breakpoints: sm: 640px, md: 768px, lg: 1024px, xl: 1280px
      if (window.innerWidth >= 1024) { // lg
        setItemsPerSlide(3);
      } else if (window.innerWidth >= 768) { // md
        setItemsPerSlide(2);
      } else {
        setItemsPerSlide(1); // mobile
      }
    };

    updateItemsPerSlide();
    window.addEventListener('resize', updateItemsPerSlide);
    return () => window.removeEventListener('resize', updateItemsPerSlide);
  }, []);

  const nextSlide = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    
    // Only collapse cards that will go out of view, auto-expand new visible cards
    const newSlide = (currentSlide + 1) % reviews.length;
    const newVisibleIndexes = Array.from({ length: itemsPerSlide }, (_, i) => (newSlide + i) % reviews.length);
    
    setExpandedCards(prev => {
      const updated = { ...prev };
      
      // Collapse cards going out of view
      Object.keys(updated).forEach(index => {
        if (!newVisibleIndexes.includes(parseInt(index))) {
          delete updated[index];
        }
      });
      
      // Auto-expand new visible cards that need truncation if enabled
      if (AUTO_EXPAND) {
        newVisibleIndexes.forEach(index => {
          if (reviews[index] && reviews[index].comment.length > MAX_COMMENT_LENGTH) {
            updated[index] = true;
          }
        });
      }

      return updated;
    });

    setCurrentSlide(newSlide);
    setTimeout(() => setIsTransitioning(false), 300);
  }, [isTransitioning, currentSlide, reviews, itemsPerSlide]);

  const prevSlide = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);

    // Only collapse cards that will go out of view, auto-expand new visible cards
    const newSlide = (currentSlide - 1 + reviews.length) % reviews.length;
    const newVisibleIndexes = Array.from({ length: itemsPerSlide }, (_, i) => (newSlide + i) % reviews.length);

    setExpandedCards(prev => {
      const updated = { ...prev };

      // Collapse cards going out of view
      Object.keys(updated).forEach(index => {
        if (!newVisibleIndexes.includes(parseInt(index))) {
          delete updated[index];
        }
      });

      // Auto-expand new visible cards that need truncation if enabled
      if (AUTO_EXPAND) {
        newVisibleIndexes.forEach(index => {
          if (reviews[index] && reviews[index].comment.length > MAX_COMMENT_LENGTH) {
            updated[index] = true;
          }
        });
      }
      
      return updated;
    });
    
    setCurrentSlide(newSlide);
    setTimeout(() => setIsTransitioning(false), 300);
  }, [isTransitioning, currentSlide, reviews, itemsPerSlide]);

  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe || isRightSwipe) {
      setHasUserInteracted(true);
      if (isLeftSwipe) {
        isRTL ? prevSlide() : nextSlide();
      } else {
        isRTL ? nextSlide() : prevSlide();
      }
    }
  };

  useEffect(() => {
    if (hasUserInteracted) return;
    const interval = setInterval(nextSlide, 5000);
    return () => clearInterval(interval);
  }, [reviews.length, hasUserInteracted, nextSlide]);

  return (
    <section 
      id={id} 
      className="relative bg-cream bg-cover bg-center bg-no-repeat py-16"
      >
      <div className="relative z-10">
        <AnimateOnScroll>
          <div className="mb-6 md:mb-12">
            <h2 className="text-3xl md:text-4xl px-8 text-mocha text-center max-w-md md:max-w-4xl mx-auto rounded-lg italic font-extrabold">
              {translations?.menu?.reviews?.title}
            </h2>
          </div>
        </AnimateOnScroll>

        <AnimateOnScroll>
          {/* Unified Carousel */}
          <div className="relative mx-auto px-20 md:px-16">
            <div className="overflow-hidden">
              <div 
                className="flex items-center transition-transform duration-300 ease-in-out"
                style={{
                  transform: isRTL ? `translateX(${currentSlide * (100 / itemsPerSlide)}%)` : `translateX(-${currentSlide * (100 / itemsPerSlide)}%)`
                }}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
              >
                {reviews.map((review, index) => (
                  <TestimonialCard 
                    key={index}
                    review={review}
                    translations={translations}
                    isExpanded={expandedCards[index] || false}
                    setIsExpanded={(expanded) => setExpandedCards(prev => ({ ...prev, [index]: expanded }))}
                    onUserInteraction={() => setHasUserInteracted(true)}
                  />
                ))}
              </div>
            </div>
            
            {/* Navigation Buttons */}
            <button 
              className="absolute start-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-mocha/20 hover:bg-mocha/30 rounded-full flex items-center justify-center cursor-pointer transition-all duration-200 z-10"
              onClick={() => {
                setHasUserInteracted(true);
                prevSlide();
              }}
              aria-label="Previous slide"
            >
              <svg className="w-5 h-5 text-white rtl:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            <button 
              className="absolute end-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-mocha/20 hover:bg-mocha/30 rounded-full flex items-center justify-center cursor-pointer transition-all duration-200 z-10"
              onClick={() => {
                setHasUserInteracted(true);
                nextSlide();
              }}
              aria-label="Next slide"
            >
              <svg className="w-5 h-5 text-white rtl:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            {/* Pagination Dots */}
            <div className="flex justify-center mt-6 gap-2">
              {reviews.map((_, index) => (
                <button
                  key={index}
                  className={`w-2 h-2 rounded-full cursor-pointer transition-all duration-200 ${
                    currentSlide === index 
                      ? 'bg-mocha'
                      : 'bg-mocha/15 hover:bg-mocha/40'
                  }`}
                  onClick={() => {
                    setHasUserInteracted(true);
                    setCurrentSlide(index);
                  }}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </AnimateOnScroll>
      </div>
    </section>
  );
};

export default ReviewsSection;